import { useCallback } from "react";
import type {
  Salesperson,
  SalespersonParams,
} from "@/hooks/api/use-salespeople";
import { buildFilterGroupFromColumnState } from "@/lib/filters";
import type { SalespersonMetrics } from "@/types/salesperson-types";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { type BaseListFilters, useListFilters } from "./use-list-filters";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Default sort settings
const DEFAULT_SORT_COLUMN = "name";
const DEFAULT_SORT_DIRECTION: SortDirection = "asc";

// SessionStorage keys
const STORAGE_KEY = "salespeople::list::filters";
const SALESPERSON_METRICS_KEY = "salespeople::list::metrics";

const DEFAULT_METRICS: Array<keyof SalespersonMetrics> = [
  "accounts_engaged",
  "total_engagements",
  "people_engaged",
  "deal_contacts_engaged",
  "opportunities_influenced_amount",
  "opportunities_influenced_won_amount",
  "opportunities_influenced_in_cycle_amount",
  "opportunities_created_post_engagement_amount",
];

// Interface for filter configuration
export interface SalespersonFilters extends BaseListFilters {
  /**
   * Type identifier for Salesperson specific filters.
   * @private Internal use only - not meant to be accessed directly
   */
  readonly _type?: "salesperson";
}

export function useSalespersonListFilters() {
  // Helper function to build query params
  const buildQueryParams = (
    filters: SalespersonFilters,
    options: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<Salesperson>[];
      limit?: number;
      metrics?: Array<keyof SalespersonMetrics>;
      metric_filters?: SalespersonParams["metric_filters"];
    },
  ): SalespersonParams => {
    const {
      searchTerm,
      columns = [],
      limit = 200,
      metrics,
      metric_filters,
    } = options;

    // Get current sort values
    let sortCol = filters.sortColumn || DEFAULT_SORT_COLUMN;

    // Fallback if sortCol no longer exists in columns
    const validSortKeys = columns.map((c) => c.key);
    if (!validSortKeys.includes(sortCol)) {
      sortCol = DEFAULT_SORT_COLUMN;
    }

    const sortDir = filters.sortDirection || DEFAULT_SORT_DIRECTION;

    // Find column definition for the current sort to get nullSort
    const column = columns.find((col) => col.key === sortCol);

    // Build base params
    const params: SalespersonParams = {
      sort_by: sortCol,
      sort_direction: sortDir === null ? undefined : sortDir,
      null_sort: column?.nullSort,
      search: searchTerm || filters.search || undefined,
      limit: limit,
    };

    // Add metrics if provided
    if (metrics) {
      params.metrics = metrics;
    }

    // Add metric filters if provided
    if (metric_filters) {
      params.metric_filters = metric_filters;
    }

    // Build StructuredFilter array ➔ FilterGroup
    const validColumnKeys = new Set(columns.map((c) => c.key.toString()));
    const group = buildFilterGroupFromColumnState(filters, validColumnKeys);
    if (group) params.filters = JSON.stringify(group);

    return params;
  };

  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams: getBaseQueryParams,
  } = useListFilters<
    SalespersonFilters,
    UnifiedTableColumn<Salesperson>,
    SalespersonParams
  >({
    storageKey: STORAGE_KEY,
    defaultSortColumn: DEFAULT_SORT_COLUMN,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    buildQueryParams,
  });

  // Wrapper for getQueryParams that accepts metrics and filters
  const getQueryParams = useCallback(
    (options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<Salesperson>[];
      limit?: number;
      metrics?: Array<keyof SalespersonMetrics>;
      metric_filters?: SalespersonParams["metric_filters"];
    }) => {
      const { metrics, metric_filters, ...restOptions } = options || {};

      // Get base query params from filters
      const params = getBaseQueryParams(restOptions) as SalespersonParams;

      // Add metrics and metric_filters if provided
      if (metrics) {
        params.metrics = metrics;
      }

      if (metric_filters) {
        params.metric_filters = metric_filters;
      }

      return params;
    },
    [getBaseQueryParams],
  );

  return {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
  };
}

/**
 * Hook for managing salesperson metrics
 * @returns Methods for getting and setting selected metrics
 */
export function useSalespersonMetrics() {
  // Get and set metrics using sessionStorage for tab-specific behavior
  const [selectedMetrics, setSelectedMetrics] = useTrackedSessionStorage<
    Array<keyof SalespersonMetrics>
  >(SALESPERSON_METRICS_KEY, DEFAULT_METRICS);

  const getDefaultMetrics = useCallback(() => DEFAULT_METRICS, []);

  return {
    selectedMetrics,
    setSelectedMetrics,
    getDefaultMetrics,
  };
}
